import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { Send, Paperclip, Mic } from 'lucide-react';
import { Button } from '../ui/Button';
import { SlashCommandPalette, SlashCommand } from './SlashCommandPalette';
import { getAllSlashCommands } from '../../data/toolCatalog';
import { AgentSelector } from './AgentSelector';
import { ModeSelector } from './ModeSelector';
import { ChatEndpoint } from '../../services/api';
interface ChatInputProps {
  onSend: (message: string) => void;
  isLoading?: boolean;
  selectedAgent?: ChatEndpoint;
  onAgentChange?: (id: ChatEndpoint) => void;
  selectedMode?: string;
  onModeChange?: (id: string) => void;
  initialValue?: string;
}
export function ChatInput({
  onSend,
  isLoading,
  selectedAgent,
  onAgentChange,
  selectedMode,
  onModeChange,
  initialValue
}: ChatInputProps) {
  const [input, setInput] = useState('');
  const [showPalette, setShowPalette] = useState(false);
  const [paletteQuery, setPaletteQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  // Set input from external edit action
  useEffect(() => {
    if (initialValue !== undefined && initialValue !== '') {
      setInput(initialValue);
      // Focus the textarea after setting value
      setTimeout(() => textareaRef.current?.focus(), 50);
    }
  }, [initialValue]);
  const allCommands = useMemo(() => {
    try {
      return getAllSlashCommands();
    } catch (e) {
      return [{
        command: '/help',
        description: 'Ver todos los comandos',
        category: 'System',
        service: 'system'
      }, {
        command: '/clear',
        description: 'Limpiar historial de chat',
        category: 'System',
        service: 'system'
      }, {
        command: '/status',
        description: 'Ver estado de conexión',
        category: 'System',
        service: 'system'
      }, {
        command: '/refresh-tokens',
        description: 'Refrescar tokens de sesión',
        category: 'System',
        service: 'system'
      }] as SlashCommand[];
    }
  }, []);
  const filteredCommands = useMemo(() => {
    if (!paletteQuery) return allCommands;
    return allCommands.filter(c => c.command.toLowerCase().includes(paletteQuery.toLowerCase()));
  }, [paletteQuery, allCommands]);
  useEffect(() => {
    setSelectedIndex(0);
  }, [paletteQuery]);
  // Auto-resize textarea
  const adjustHeight = useCallback(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    textarea.style.height = 'auto';
    const maxHeight = 160; // ~6 lines
    textarea.style.height = `${Math.min(textarea.scrollHeight, maxHeight)}px`;
    textarea.style.overflowY = textarea.scrollHeight > maxHeight ? 'auto' : 'hidden';
  }, []);
  useEffect(() => {
    adjustHeight();
  }, [input, adjustHeight]);
  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const val = e.target.value;
    setInput(val);
    if (val.startsWith('/')) {
      setShowPalette(true);
      setPaletteQuery(val.slice(1).split(' ')[0]);
    } else {
      setShowPalette(false);
    }
  };
  const handleCommandSelect = (cmd: string) => {
    setInput(cmd + ' ');
    setShowPalette(false);
    textareaRef.current?.focus();
  };
  const handleSend = () => {
    if (input.trim() && !isLoading) {
      onSend(input.trim());
      setInput('');
      setShowPalette(false);
      // Reset height after clearing
      requestAnimationFrame(() => {
        if (textareaRef.current) {
          textareaRef.current.style.height = 'auto';
        }
      });
    }
  };
  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    // Slash command palette navigation
    if (showPalette && filteredCommands.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedIndex(s => Math.min(s + 1, filteredCommands.length - 1));
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedIndex(s => Math.max(0, s - 1));
        return;
      }
      if (e.key === 'Enter' && !e.shiftKey && !e.ctrlKey) {
        e.preventDefault();
        handleCommandSelect(filteredCommands[selectedIndex].command);
        return;
      }
      if (e.key === 'Escape') {
        setShowPalette(false);
        return;
      }
    }
    // Enter = send, Ctrl+Enter or Shift+Enter = new line
    if (e.key === 'Enter') {
      if (e.ctrlKey || e.shiftKey) {
        // Allow new line — default textarea behavior
        return;
      }
      // Send message
      e.preventDefault();
      handleSend();
    }
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    handleSend();
  };
  return <div className="relative w-full" data-id="element-1866">
      {showPalette && <SlashCommandPalette commands={filteredCommands} selectedIndex={selectedIndex} onSelect={handleCommandSelect} data-id="element-1867" />}

      <form onSubmit={handleSubmit} className="relative flex items-end gap-2 bg-slate-800 p-2 rounded-xl border border-slate-700 shadow-lg shadow-slate-950/50 focus-within:ring-2 focus-within:ring-indigo-500/50 focus-within:border-indigo-500/50 transition-all" data-id="element-1868">
        <Button type="button" variant="ghost" size="icon" className="text-slate-500 hover:text-slate-300 hover:bg-slate-700 flex-shrink-0 mb-0.5" data-id="element-1869">
          <Paperclip size={20} data-id="element-1870" />
        </Button>

        <textarea ref={textareaRef} value={input} onChange={handleInputChange} onKeyDown={handleKeyDown} placeholder="Escribí / para ver comandos o preguntá algo... (Ctrl+Enter = nueva línea)" className="flex-1 bg-transparent border-none focus:ring-0 text-sm placeholder:text-slate-500 text-slate-200 resize-none min-h-[36px] max-h-[160px] py-2 leading-relaxed scrollbar-thin scrollbar-thumb-slate-600" disabled={isLoading} autoComplete="off" rows={1} data-id="element-1871" />

        {input.trim() ? <Button type="submit" size="icon" variant="primary" isLoading={isLoading} className="rounded-lg h-8 w-8 flex-shrink-0 mb-0.5" data-id="element-1872">
            <Send size={16} data-id="element-1873" />
          </Button> : <Button type="button" variant="ghost" size="icon" className="text-slate-500 hover:text-slate-300 hover:bg-slate-700 flex-shrink-0 mb-0.5" data-id="element-1874">
            <Mic size={20} data-id="element-1875" />
          </Button>}
      </form>

      <div className="flex items-center justify-between mt-2 px-1" data-id="element-1876">
        <div className="flex items-center gap-1.5" data-id="element-1877">
          {selectedMode && onModeChange && <ModeSelector selectedModeId={selectedMode} onSelect={onModeChange} data-id="element-1878" />}
          {selectedAgent && onAgentChange && <AgentSelector selectedAgentId={selectedAgent} onSelect={onAgentChange} data-id="element-1879" />}
        </div>
        <p className="text-[10px] text-slate-600" data-id="element-1880">
          Enter para enviar · Ctrl+Enter para nueva línea
        </p>
      </div>
    </div>;
}